import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  ArrowUpRight, 
  ArrowDownLeft, 
  History, 
  RefreshCw, 
  CheckCircle2, 
  XCircle,
  Plus,
  Search,
  DollarSign,
  Wallet,
  ArrowRightLeft,
  LayoutGrid
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../Config';

export default function TesoreriaBoveda() {
  const { user, token, handleAuthError } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bovedas, setBovedas] = useState([]);
  const [allCajas, setAllCajas] = useState([]);
  const [transferencias, setTransferencias] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [nuevaTransferencia, setNuevaTransferencia] = useState({
    cajaOrigenId: '',
    cajaDestinoId: '',
    monto: '',
    tipo: 'PROVISION',
    observacion: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const sucursalId = user?.sucursalId;
      const queryParam = sucursalId ? `?sucursalId=${sucursalId}` : '';

      // Fetch All Cajas
      const resAll = await fetch(`${API_BASE_URL}/tesoreria/boveda/cajas${queryParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleAuthError(resAll)) return;
      if (resAll.ok) {
        const data = await resAll.json();
        setAllCajas(data);
        setBovedas(data.filter(c => c.tipoCaja === 'BOVEDA_CENTRAL' || c.tipoCaja === 'BOVEDA'));
      }

      // Fetch Pending Transfers
      const resTransf = await fetch(`${API_BASE_URL}/tesoreria/boveda/pendientes${queryParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resTransf.ok) {
        const data = await resTransf.json();
        setTransferencias(data);
      }
    } catch (err) {
      console.error("Error fetching boveda data", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTransferencia = async (e) => {
    e.preventDefault();
    try {
      let url = '';
      if (nuevaTransferencia.tipo === 'PROVISION') {
        url = `${API_BASE_URL}/tesoreria/boveda/solicitar-provision?bovedaId=${nuevaTransferencia.cajaOrigenId}&cajaDestinoId=${nuevaTransferencia.cajaDestinoId}&monto=${nuevaTransferencia.monto}&usuarioId=${user.id}`;
      } else {
        url = `${API_BASE_URL}/tesoreria/boveda/solicitar-retiro?cajaOrigenId=${nuevaTransferencia.cajaOrigenId}&bovedaId=${nuevaTransferencia.cajaDestinoId}&monto=${nuevaTransferencia.monto}&usuarioId=${user.id}`;
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setShowTransferModal(false);
        setNuevaTransferencia({ cajaOrigenId: '', cajaDestinoId: '', monto: '', tipo: 'PROVISION', observacion: '' });
        fetchData();
      }
    } catch (err) {
      console.error("Error creating transfer", err);
    }
  };

  const handleProcesarTransferencia = async (id, aceptado) => {
    try {
      const res = await fetch(`${API_BASE_URL}/tesoreria/boveda/procesar-transferencia?transferenciaId=${id}&usuarioId=${user.id}&aceptado=${aceptado}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error("Error processing transfer", err);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'Gs. 0';
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            Gestión de Bóveda y Tesoro
          </h1>
          <p className="text-slate-500 font-bold uppercase text-[10px] tracking-[0.2em] mt-1 flex items-center gap-2">
            Centralized Cash Management <LayoutGrid className="w-3 h-3" /> Branch Operations
          </p>
        </div>
        
        <button 
          onClick={() => setShowTransferModal(true)}
          className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-sm tracking-widest uppercase hover:bg-blue-600 transition-all shadow-xl shadow-slate-900/10 flex items-center gap-3"
        >
          <ArrowRightLeft className="w-5 h-5" />
          Nueva Transferencia
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Boveda Balances */}
        <div className="lg:col-span-2 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bovedas.map((b) => (
              <div key={b.id} className="bg-white p-8 rounded-[2rem] border-2 border-slate-900 shadow-[8px_8px_0px_0px_rgba(15,23,42,1)] transition-transform hover:-translate-y-1">
                <div className="flex justify-between items-start mb-6">
                  <div className="p-3 bg-blue-50 rounded-2xl border border-blue-100">
                    <Wallet className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">Activa</span>
                </div>
                <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-1">{b.nombreCaja}</h3>
                <p className="text-3xl font-black text-slate-900 leading-none mb-4">{formatCurrency(b.saldoActual)}</p>
                <div className="pt-4 border-t border-slate-100 flex items-center gap-2 text-slate-500 text-[10px] font-bold">
                  <Building2 className="w-3 h-3" />
                  {b.sucursal?.nombre || 'MATRIZ'}
                </div>
              </div>
            ))}
          </div>

          {/* Transfer History Table */}
          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl shadow-slate-200/40 overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h2 className="text-lg font-black text-slate-800 flex items-center gap-3">
                <History className="w-5 h-5 text-slate-400" />
                Transferencias Recientes
              </h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input 
                  type="text" 
                  placeholder="Buscar..." 
                  className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:ring-2 focus:ring-blue-500 outline-none w-64"
                />
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                    <th className="px-6 py-4">Fecha / Hora</th>
                    <th className="px-6 py-4">Tipo</th>
                    <th className="px-6 py-4">Origen / Destino</th>
                    <th className="px-6 py-4 text-right">Monto</th>
                    <th className="px-6 py-4 text-center">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  <tr className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 col-span-5 text-center py-20">
                      <History className="w-12 h-12 text-slate-100 mx-auto mb-4" />
                      <p className="text-sm font-bold text-slate-400 italic">No hay historial de transferencias procesadas hoy.</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pending Requests Column */}
        <div className="space-y-6">
          <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-xl font-black mb-6 flex items-center gap-3">
                <ArrowRightLeft className="w-6 h-6 text-blue-400" />
                Solicitudes Pendientes
              </h2>
              
              <div className="space-y-4">
                {transferencias.length === 0 ? (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
                    <CheckCircle2 className="w-8 h-8 text-white/20 mx-auto mb-3" />
                    <p className="text-xs font-bold text-slate-400">Todo al día. No hay solicitudes pendientes de autorización.</p>
                  </div>
                ) : (
                  transferencias.map((t) => (
                    <div key={t.id} className="bg-white/10 border border-white/10 rounded-[1.5rem] p-5 backdrop-blur-sm">
                      <div className="flex justify-between items-start mb-3">
                        <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider ${t.tipo === 'PROVISION' ? 'bg-blue-500 text-white' : 'bg-amber-500 text-white'}`}>
                          {t.tipo}
                        </span>
                        <span className="text-[10px] text-slate-400 font-bold">{new Date(t.timestampSolicitud).toLocaleTimeString()}</span>
                      </div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1">
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">De</p>
                          <p className="text-xs font-black truncate">{t.cajaOrigen?.nombreCaja || 'N/A'}</p>
                        </div>
                        <ArrowRightLeft className="w-4 h-4 text-slate-700" />
                        <div className="flex-1 text-right">
                          <p className="text-[9px] font-black text-slate-500 uppercase tracking-tighter">A</p>
                          <p className="text-xs font-black truncate">{t.cajaDestino?.nombreCaja || 'N/A'}</p>
                        </div>
                      </div>
                      <p className="text-xl font-black mb-5">{formatCurrency(t.monto)}</p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <button 
                          onClick={() => handleProcesarTransferencia(t.id, false)}
                          className="py-3 bg-white/5 hover:bg-red-500/20 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all border border-white/10"
                        >
                          Rechazar
                        </button>
                        <button 
                          onClick={() => handleProcesarTransferencia(t.id, true)}
                          className="py-3 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-900/50"
                        >
                          Aprobar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div className="absolute bottom-0 right-0 translate-y-1/4 translate-x-1/4 w-48 h-48 bg-blue-600/20 rounded-full blur-3xl"></div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-[2rem] border border-slate-200 p-8">
            <h3 className="font-black text-slate-800 mb-6 uppercase text-[10px] tracking-widest">Resumen Tesorería</h3>
            <div className="space-y-6">
              {[
                { label: 'Efectivo en Bóveda', value: formatCurrency(bovedas.reduce((acc, b) => acc + (b.saldoActual || 0), 0)), color: 'blue' },
                { label: 'Efectivo en Cajas', value: formatCurrency(0), color: 'slate' },
                { label: 'Total Cartera Hoy', value: formatCurrency(125000000), color: 'slate' }
              ].map((s, i) => (
                <div key={i} className="flex justify-between items-end border-b border-slate-50 pb-4 last:border-0">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter mb-1">{s.label}</p>
                    <p className="text-lg font-black text-slate-900 leading-none">{s.value}</p>
                  </div>
                  <div className={`w-8 h-8 rounded-lg bg-${s.color}-50 flex items-center justify-center`}>
                    <DollarSign className={`w-4 h-4 text-${s.color}-600`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* Modal Nueva Transferencia */}
      {showTransferModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-premium-in">
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
            <div className="p-8 bg-slate-900 text-white flex justify-between items-center">
              <div>
                <h3 className="text-xl font-black flex items-center gap-3">
                  <ArrowRightLeft className="w-6 h-6 text-blue-400" />
                  Nueva Transferencia
                </h3>
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">Inter-Vault Movement</p>
              </div>
              <button onClick={() => setShowTransferModal(false)} className="p-2 hover:bg-white/10 rounded-full transition-all">
                <XCircle className="w-7 h-7" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitTransferencia} className="p-10 space-y-8">
              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-2xl">
                <button
                  type="button"
                  onClick={() => setNuevaTransferencia({...nuevaTransferencia, tipo: 'PROVISION'})}
                  className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${nuevaTransferencia.tipo === 'PROVISION' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Provisión (A Caja)
                </button>
                <button
                  type="button"
                  onClick={() => setNuevaTransferencia({...nuevaTransferencia, tipo: 'SOBRANTE'})}
                  className={`py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${nuevaTransferencia.tipo === 'SOBRANTE' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Sobrante (A Bóveda)
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Bóveda Central</label>
                  <select 
                    required
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-black text-slate-800 outline-none focus:ring-4 focus:ring-blue-500/10 transition-all"
                    value={nuevaTransferencia.tipo === 'PROVISION' ? nuevaTransferencia.cajaOrigenId : nuevaTransferencia.cajaDestinoId}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (nuevaTransferencia.tipo === 'PROVISION') setNuevaTransferencia({...nuevaTransferencia, cajaOrigenId: val});
                      else setNuevaTransferencia({...nuevaTransferencia, cajaDestinoId: val});
                    }}
                  >
                    <option value="">Seleccione Bóveda...</option>
                    {bovedas.map(b => (
                      <option key={b.id} value={b.id}>{b.nombreCaja} ({formatCurrency(b.saldoActual)})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Caja Ventanilla</label>
                  <select 
                    required
                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-black text-slate-800 outline-none focus:ring-4 focus:ring-blue-500/10 transition-all"
                    value={nuevaTransferencia.tipo === 'PROVISION' ? nuevaTransferencia.cajaDestinoId : nuevaTransferencia.cajaOrigenId}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (nuevaTransferencia.tipo === 'PROVISION') setNuevaTransferencia({...nuevaTransferencia, cajaDestinoId: val});
                      else setNuevaTransferencia({...nuevaTransferencia, cajaOrigenId: val});
                    }}
                  >
                    <option value="">Seleccione Destino...</option>
                    {allCajas.filter(c => c.tipoCaja === 'VENTANILLA').map(c => (
                      <option key={c.id} value={c.id}>{c.nombreCaja} - {c.usuarioAsignado?.nombreUsuario || 'Sin Asignar'}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Monto a Transferir (Gs.)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 w-5 h-5" />
                    <input 
                      type="number"
                      required
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-black text-2xl text-slate-800 outline-none focus:ring-4 focus:ring-blue-500/10 transition-all"
                      placeholder="0"
                      value={nuevaTransferencia.monto}
                      onChange={(e) => setNuevaTransferencia({...nuevaTransferencia, monto: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="flex-1 py-4 text-slate-400 font-black text-xs uppercase tracking-widest hover:text-slate-600"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-600 shadow-xl shadow-slate-900/10 transition-all"
                >
                  Solicitar Transferencia
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
